import { action, runInAction, makeAutoObservable } from 'mobx';

import { Site, SiteBody, SiteEntryProps, WatcherrApiError } from '@ixicare/resources';
import SiteStore from './Site';
import { UserAction } from '~config/toastNotification';
import { State } from '~@types/store';
import { humaniseErrorToMessage } from '~utils/error';
import { t } from '~i18n';
import PersistentStoreService from '~services/PersistentStoreService';

const LAST_SITE_PER_USER_KEY = 'ixicare.lastSitePerUser';
type LastSiteMap = Record<number, string>;

const sortSitesByName = (sites: SiteStore[]) => sites.sort((a, b) => a.name.localeCompare(b.name));

class SitesStore {
  rootStore: State;
  sites: SiteStore[] = [];
  selectedSiteUuid: string | null = null;
  loaded: boolean = false;
  loading: boolean = false;
  loadedForUserId: number | null = null;
  error?: WatcherrApiError;

  constructor(rootStore: State) {
    makeAutoObservable(this, {
      rootStore: false,
      loadSites: action.bound,
      reloadSites: action.bound,
      setSelectedSiteUuid: action.bound,
      createBuilding: action.bound,
      reset: action.bound,
    });
    this.rootStore = rootStore;
  }

  get selectedSite(): SiteStore | null {
    return this.sites.find((s) => s.uuid === this.selectedSiteUuid) ?? null;
  }

  setSelectedSiteUuid(uuid: string | null) {
    this.selectedSiteUuid = uuid && this.sites.some((s) => s.uuid === uuid) ? uuid : null;
    if (this.selectedSiteUuid && this.loadedForUserId !== null) {
      const map = PersistentStoreService.get<LastSiteMap>(LAST_SITE_PER_USER_KEY) ?? {};
      map[this.loadedForUserId] = this.selectedSiteUuid;
      PersistentStoreService.set(LAST_SITE_PER_USER_KEY, map);
    }
  }

  async loadSites(preferredUuid?: string | null, userId?: number): Promise<void> {
    if (this.loading) return;
    if (this.loaded && (userId === undefined || this.loadedForUserId === userId)) return;

    this.loading = true;
    try {
      const entrySites = await Site.getSites();
      runInAction(() => {
        this.sites = sortSitesByName(entrySites.map((s: SiteEntryProps) => new SiteStore(this.rootStore, s)));
        this.loaded = true;
        if (userId !== undefined) {
          this.loadedForUserId = userId;
          const lastForUser = (PersistentStoreService.get<LastSiteMap>(LAST_SITE_PER_USER_KEY) ?? {})[userId];
          const candidate = lastForUser ?? preferredUuid ?? undefined;
          this.selectedSiteUuid =
            candidate && this.sites.some((s) => s.uuid === candidate) ? candidate : (this.sites[0]?.uuid ?? null);
        } else if (!this.selectedSiteUuid && this.sites.length > 0) {
          this.selectedSiteUuid = this.sites[0].uuid;
        }
      });
    } catch (res: any) {
      runInAction(() => {
        this.error = res;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async reloadSites(): Promise<void> {
    try {
      const entrySites = await Site.getSites();
      runInAction(() => {
        this.sites = sortSitesByName(entrySites.map((s: SiteEntryProps) => new SiteStore(this.rootStore, s)));
        this.loaded = true;
      });
    } catch (res: any) {
      runInAction(() => {
        this.error = res;
      });
    }
  }

  reset() {
    this.sites = [];
    this.selectedSiteUuid = null;
    this.loaded = false;
    this.loadedForUserId = null;
  }

  createBuilding(body: SiteBody) {
    return Site.createSite(body)
      .then((site) => {
        site.name = body.name;
        site.address = body.address;
        site.city = body.city;
        site.postalCode = body.postalCode;
        site.country = body.country;
        site.lat = body.lat;
        site.long = body.long;
        site.alertContactName = body.alertContactName;
        site.alertContactPhoneNumber = body.alertContactPhoneNumber;
        site.alertContactEmail = body.alertContactEmail;
        site.doorCode = body.doorCode;
        site.vatNumber = body.vatNumber;
        site.organizationId = body.organizationId ?? null;

        const building = new SiteStore(this.rootStore, site);

        runInAction(() => {
          this.sites.push(building);
          sortSitesByName(this.sites);
          this.rootStore.notifications.addUserActionNotification({
            type: UserAction.SiteCreate,
            notificationType: 'success',
            message: t('alerts.site.create.success', { siteName: body.name }),
          });
        });
        return building;
      })
      .catch((res) => {
        this.error = res;
        throw res;
      });
  }

  archiveBuilding = (siteUuid: string) => {
    return new Promise<SiteEntryProps>((resolve, reject) => {
      Site.archiveSite(siteUuid)
        .then((data) => {
          runInAction(() => {
            this.rootStore.notifications.addUserActionNotification({
              type: UserAction.SiteArchive,
              notificationType: 'success',
              message: t('alerts.site.archive.success'),
            });
            this.sites = this.sites.filter((site) => site.uuid !== siteUuid);
            if (this.selectedSiteUuid === siteUuid) {
              this.selectedSiteUuid = this.sites[0]?.uuid ?? null;
            }
          });
          resolve(data);
        })
        .catch((res) => {
          runInAction(() => {
            this.error = res;
            this.rootStore.notifications.addUserActionNotification({
              type: UserAction.SiteArchive,
              notificationType: 'error',
              message: this.error ? humaniseErrorToMessage(this.error) : t('alerts.site.archive.failure'),
            });
          });

          reject();
        });
    });
  };

  resetDemokit = async (siteId: number) => {
    const data = await Site.resetDemokit(siteId);
    runInAction(() => {
      this.reset();
    });
    await this.loadSites();
    runInAction(() => {
      const target = this.sites.find((site) => site.id === siteId);
      this.selectedSiteUuid = target?.uuid ?? null;
    });
    return data;
  };
}

export default SitesStore;
